import React, { useEffect, useState } from 'react';

import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { ButtonInput, LogForm, RadioGroupInput } from '@components/Form';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { Layout } from '@components/Layout';
import { SpinnerModal } from '@components/SpinnerModal';
import { Stepper } from '@components/Stepper';
import { SemiAutoCompleteOption } from '@components/TextBox';
import {
  HandleCNZipcodeChange,
  HandleJPZipcodeChange,
} from '@components/utilities/Contact/ContactForm';
import {
  MembershipTextInput,
  NameTextInput,
  TextInput,
} from '@components/utilities/MembershipForm/Form';
import { Split } from '@components/utilities/MembershipForm/Split';
import { steps, strings as membershipStrings } from '@data/membershipForm';
import { usePatchCompany } from '@hooks/mutation/usePatchCompany';
import { usePatchUser } from '@hooks/mutation/usePatchUser';
import { useCompany } from '@hooks/queries/useCompany';
import { useUser } from '@hooks/queries/useUser';
import { MembershipFormFields } from '@models/MembershipForm';
import { useButtonStyles, useEnabledSteps } from '@pages/utilities/MembershipForm/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, Field, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik, useField, useFormikContext } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import { Prompt, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '情報入力',
    informationInputPrompt: '情報を入力してください。',
    transactionCondition: 'お取引条件',
    transactionConditionOk: 'このままでいい',
    transactionConditionNo: '取引条件について相談したい',
    zipcodePlaceholder: {
      ja: '日本の郵便番号を入力してください',
      zh: '6桁の中国の郵便番号を入力してください　例：523000',
    },
    address1Placeholder: {
      ja: '都道府県・市区町村・番地を入力してください',
      zh: '中国の都市名を入力してください　例：Dongguan, Guangdong',
    },
    address2Placeholder: {
      ja: '建物名・号室を入力してください',
      zh: '中国の街道・建物名・号室などを入力してください　例：No.○, ○○ Road',
    },
    prev: '戻る',
    representer: '代表者',
    principal: '担当者',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  informationInputPrompt: {
    ...common.middleText,
    color: theme.palette.grey['500'],
    padding: '40px 0',
  },
  transactionConditionPrompt: {
    ...common.middleText,
    color: theme.palette.grey['800'],
    padding: '40px 0',
    whiteSpace: 'pre-wrap',
  },
  transactionInquiryPrompt: {
    color: theme.palette.grey['800'],
  },
  sharedInfoPrompt: {
    ...common.middleText,
    color: theme.palette.grey['800'],
    whiteSpace: 'pre-wrap',
  },
  sectionContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '3rem 0',
  },
  radioGroupInput: {
    ...common.flexInline,
    flex: 1,
    justifyContent: 'space-around',
  },
  checkedInputContainer: {
    position: 'relative',
  },
  checkMark: {
    position: 'absolute',
    top: '11px',
    left: '-35px',
  },
  addressTextInputContainer1: {
    flexBasis: '230px',
    flex: 1,
  },
  addressTextInputContainer2: {
    flex: 1,
  },
}));

const AutoFillTransactionConditionDetail = () => {
  const [transactionConditionField] = useField(
    MembershipFormFields.shared.transactionCondition.name,
  );

  const [, , transactionConditionDetailHelpers] = useField(
    MembershipFormFields.shared.transactionConditionDetail.name,
  );

  useEffect(() => {
    if (transactionConditionField.value === 'Y') {
      transactionConditionDetailHelpers.setValue(membershipStrings.transactionCondition);
    } else {
      transactionConditionDetailHelpers.setValue(membershipStrings.transactionConditionInquiry);
    }
  }, [transactionConditionField.value]);

  return null;
};

const AutoFillPrincipalName = () => {
  const [principalFirstNameField] = useField(MembershipFormFields.shared.principalFirstName.name);
  const [principalLastNameField] = useField(MembershipFormFields.shared.principalLastName.name);

  const [, , principalNameHelpers] = useField(MembershipFormFields.shared.principalName.name);

  useEffect(() => {
    principalNameHelpers.setValue(
      `${principalLastNameField.value} ${principalFirstNameField.value}`,
    );
  }, [principalFirstNameField.value, principalLastNameField.value]);

  return null;
};

interface AddressFieldsProps {
  isSharedInfo?: boolean;
}

const AddressFields: React.FC<AddressFieldsProps> = (props) => {
  const { isSharedInfo } = props;
  const classes = { ...useStyles(), ...useCommonStyles() };
  const [zipCodeChoices, setZipCodeChoices] = useState<SemiAutoCompleteOption[]>([]);
  const [addressChoices, setAddressChoices] = useState<SemiAutoCompleteOption[]>([]);
  const formik = useFormikContext();
  const [countryField] = useField(MembershipFormFields.shared.country.name);
  const isJPAddress = countryField?.value === 'jp';

  return (
    <>
      {isJPAddress ? (
        <>
          <HandleJPZipcodeChange
            zipCodeFieldName={MembershipFormFields.shared.zipcode.name}
            addressFieldName={MembershipFormFields.shared.address1.name}
          />
          <MembershipTextInput
            field={MembershipFormFields.shared.zipcode}
            textInputProps={{
              disabled: isSharedInfo,
              required: !isSharedInfo,
              placeholder: isSharedInfo ? '' : strings.zipcodePlaceholder.ja,
              noErrorMessage: isSharedInfo,
            }}
          />
          <MembershipTextInput
            field={MembershipFormFields.shared.address1}
            textInputProps={{
              disabled: isSharedInfo,
              required: !isSharedInfo,
              placeholder: isSharedInfo ? '' : strings.address1Placeholder.ja,
              noErrorMessage: isSharedInfo,
            }}
          />
          <MembershipTextInput
            field={MembershipFormFields.shared.address2}
            textInputProps={{
              disabled: isSharedInfo,
              required: !isSharedInfo,
              placeholder: isSharedInfo ? '' : strings.address2Placeholder.ja,
              noErrorMessage: isSharedInfo,
            }}
          />
        </>
      ) : (
        <>
          <HandleCNZipcodeChange
            zipCodeFieldName={MembershipFormFields.shared.zipcode.name}
            addressFieldName={MembershipFormFields.shared.city.name}
            addressCNFieldName="address1_cn"
            onZipCodeSemiAutoCompleteOptions={setZipCodeChoices}
            onAddressSemiAutoCompleteOptions={setAddressChoices}
          />
          <div className={classes.flexInline}>
            <div className={classes.addressTextInputContainer1}>
              <MembershipTextInput
                field={MembershipFormFields.shared.city}
                textInputProps={{
                  placeholder: isSharedInfo
                    ? ''
                    : MembershipFormFields.shared.city.options.placeholder,
                  required: !isSharedInfo,
                  disabled: isSharedInfo,
                  noErrorMessage: isSharedInfo,
                  withSemiAutoComplete: true,
                  semiAutoCompleteOptions: addressChoices,
                  onSemiAutoCompleteOptionSelect: (option: SemiAutoCompleteOption) => {
                    const { address, zipcode } = option.value;
                    formik.setFieldValue('address1', address, true);
                    formik.setFieldValue('zipcode', zipcode, true);
                  },
                }}
              />
            </div>
            <HorizontalSpace width={20} />
            <div className={classes.addressTextInputContainer2}>
              <TextInput
                noLabel
                noErrorMessage={isSharedInfo}
                required={!isSharedInfo}
                disabled={isSharedInfo}
                name={MembershipFormFields.shared.zipcode.name}
                withSemiAutoComplete
                semiAutoCompleteOptions={zipCodeChoices}
                placeholder={isSharedInfo ? '' : strings.zipcodePlaceholder.zh}
                onSemiAutoCompleteOptionSelect={(option: SemiAutoCompleteOption) => {
                  const { address, zipcode } = option.value;
                  formik.setFieldValue('address1', address, true);
                  formik.setFieldValue('zipcode', zipcode, true);
                }}
              />
            </div>
          </div>
          <MembershipTextInput
            field={MembershipFormFields.shared.address}
            textInputProps={{
              placeholder: isSharedInfo ? '' : strings.address2Placeholder.zh,
              required: !isSharedInfo,
              disabled: isSharedInfo,
              noErrorMessage: isSharedInfo,
            }}
          />
        </>
      )}
    </>
  );
};

export const Information: React.FC = () => {
  const user = useUser();
  const company = useCompany();

  const patchUser = usePatchUser();
  const patchCompany = usePatchCompany();
  const enabledSteps = useEnabledSteps();

  const history = useHistory();

  const isUpdating = patchUser.isLoading || patchCompany.isLoading;

  const classes = {
    ...useCommonStyles(),
    ...useButtonStyles(),
    ...useStyles(),
  };

  const renderNameTextField = (
    label: string,
    lastName: Field<any>,
    firstName: Field<any>,
    props?: Partial<React.ComponentProps<typeof TextInput>>,
  ) => {
    return (
      <NameTextInput
        name={lastName.name}
        firstNameFieldName={firstName.name}
        label={label}
        required={!lastName.options.omittable}
        {...props}
      />
    );
  };

  return (
    <Layout
      loading={user.isLoading || company.isLoading}
      errors={[user.error, company.error]}
      title={strings.title}
    >
      {() => (
        <Container>
          <SpinnerModal open={isUpdating} />
          <Formik
            validate={(values) => {
              const schema = MembershipFormFields.shared.toMembershipFormSchema(
                values.country === 'jp',
              );
              return createValidate(schema)(values);
            }}
            initialValues={MembershipFormFields.shared.mapCompanyUserToMembershipForm(
              company.data!,
              user.data!,
            )}
            validateOnMount
            onSubmit={async (values) => {
              const schema = MembershipFormFields.shared.toMembershipFormSchema(
                values.country === 'jp',
              );
              const validation = schema.decode(values);

              if (!isRight(validation)) {
                throw new Error('Information: Unexpected erroneus form values. ');
              }

              const patchUserData = MembershipFormFields.shared.mapPrincipalFormToPatchPrincipal(
                validation.right,
              );

              const patchCompanyData = MembershipFormFields.shared.mapCompanyFormToCompany(
                validation.right,
              );

              try {
                await Promise.all([
                  patchUser.mutateAsync(patchUserData),
                  patchCompany.mutateAsync(patchCompanyData),
                ]);
              } catch (e) {
                if (e instanceof FormError) {
                  // eslint-disable-next-line no-alert
                  alert(e.getDisplayMessage());
                }
                throw e;
              }

              localStorage.removeItem('/utilities/MembershipForm/Information');
              history.push('/utilities/MembershipForm/Checklist');
              window.scrollTo(0, 0);
            }}
          >
            {({ dirty, isSubmitting }) => (
              <Form className={classes.flexColumn}>
                <Prompt when={dirty && !isSubmitting} message={() => formStrings.promptLeaving} />

                <LogForm name="information" disabled />
                <Stepper steps={steps} value={steps[1].name} enabledSteps={enabledSteps} />
                <div className={classes.informationInputPrompt}>
                  {strings.informationInputPrompt}
                </div>
                <Split title={membershipStrings.companyInfo} />
                <div className={classes.sectionContainer}>
                  <MembershipTextInput field={MembershipFormFields.shared.companyName} />
                  <MembershipTextInput field={MembershipFormFields.shared.englishName} />
                  <MembershipTextInput field={MembershipFormFields.shared.corporateNumber} />
                  <MembershipTextInput field={MembershipFormFields.shared.country} />
                  <AddressFields />
                  <MembershipTextInput field={MembershipFormFields.shared.phone} />
                  <MembershipTextInput field={MembershipFormFields.shared.fax} />
                  <MembershipTextInput field={MembershipFormFields.shared.startDate} />
                  {renderNameTextField(
                    strings.representer,
                    MembershipFormFields.shared.representerLastName,
                    MembershipFormFields.shared.representerFirstName,
                  )}
                  <MembershipTextInput field={MembershipFormFields.shared.legalCapital} />
                  <MembershipTextInput field={MembershipFormFields.shared.industry} />
                  <MembershipTextInput field={MembershipFormFields.shared.employeeCount} />
                </div>
                <Split title={membershipStrings.principalInfo} />
                <div className={classes.sectionContainer}>
                  {renderNameTextField(
                    strings.principal,
                    MembershipFormFields.shared.principalLastName,
                    MembershipFormFields.shared.principalFirstName,
                  )}
                  <MembershipTextInput field={MembershipFormFields.shared.principalCellphone} />
                  <MembershipTextInput field={MembershipFormFields.shared.principalLandline} />
                  <MembershipTextInput
                    field={MembershipFormFields.shared.principalEmail}
                    textInputProps={{
                      disabled: true,
                    }}
                  />

                  <MembershipTextInput field={MembershipFormFields.shared.snsId} />
                </div>
                <Split title={membershipStrings.D2DMemberSharedInfo} />
                <div className={classes.sectionContainer}>
                  <div className={classes.sharedInfoPrompt}>
                    {membershipStrings.sharedInfoPrompt}
                  </div>
                  <MembershipTextInput
                    field={MembershipFormFields.shared.companyName}
                    textInputProps={{ disabled: true, required: false, placeholder: '' }}
                  />
                  <AddressFields isSharedInfo />
                  <MembershipTextInput
                    field={MembershipFormFields.shared.principalName}
                    textInputProps={{ disabled: true, required: false, placeholder: '' }}
                  />
                  <MembershipTextInput
                    field={MembershipFormFields.shared.principalCellphone}
                    textInputProps={{ disabled: true, required: false, placeholder: '' }}
                  />
                  <MembershipTextInput
                    field={MembershipFormFields.shared.principalEmail}
                    textInputProps={{ disabled: true, required: false, placeholder: '' }}
                  />
                  <MembershipTextInput
                    field={MembershipFormFields.shared.snsId}
                    textInputProps={{ disabled: true, required: false, placeholder: '' }}
                  />
                </div>

                <AutoFillPrincipalName />

                <Split title={strings.transactionCondition} />
                <div className={classes.transactionConditionPrompt}>
                  {membershipStrings.transactionCondition}
                  {'\n'}
                  {'\n'}
                  <span className={classes.transactionInquiryPrompt}>
                    {membershipStrings.transactionInquiryPrompt}
                  </span>
                </div>

                <RadioGroupInput
                  noLabel
                  label=""
                  name="transactionCondition"
                  iconSize="small"
                  radioGroupClasses={{
                    root: classes.radioGroupInput,
                  }}
                  formControlLabelClasses={{
                    label: classes.middleTextImportant,
                  }}
                  options={[
                    {
                      value: 'Y',
                      label: strings.transactionConditionOk,
                    },
                    {
                      value: 'N',
                      label: strings.transactionConditionNo,
                    },
                  ]}
                />

                <AutoFillTransactionConditionDetail />

                <div className={classes.buttons}>
                  <Button
                    text={strings.prev}
                    color="primary"
                    className={clsx(classes.button, classes.prev)}
                    onClick={() => history.push('/utilities/MembershipForm/PolicyAgreement')}
                  />
                  <ButtonInput
                    text={formStrings.next}
                    color="primary"
                    className={clsx(classes.button, classes.next)}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </Container>
      )}
    </Layout>
  );
};
